import type {
  ApiList,
  Client,
  ClientContact,
  Context,
  FeedItem,
  FunctionRow,
  MyWork,
  Notification,
  OrgUser,
  Role,
  SavedView,
  Tag,
  TaskDetail,
  TaskListQuery,
  TaskSummary,
  Team,
} from '@pm/schemas';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import { myWorkRange } from './format';

/** Serializa filtros em query string (arrays viram CSV; undefined some). */
export function qs(params: Record<string, unknown>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) {
      if (v.length) sp.set(k, v.join(','));
    } else sp.set(k, typeof v === 'string' ? v : JSON.stringify(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export const keys = {
  tasks: (q: Partial<TaskListQuery>) => ['tasks', q] as const,
  task: (id: string) => ['task', id] as const,
  feed: (id: string) => ['task-feed', id] as const,
  myWork: ['my-work'] as const,
  users: (q?: Record<string, unknown>) => ['users', q ?? {}] as const,
  roles: ['roles'] as const,
  teams: ['teams'] as const,
  functions: ['functions'] as const,
  clients: (q?: Record<string, unknown>) => ['clients', q ?? {}] as const,
  client: (id: string) => ['client', id] as const,
  contacts: (id: string) => ['client-contacts', id] as const,
  contexts: (q?: Record<string, unknown>) => ['contexts', q ?? {}] as const,
  tags: ['tags'] as const,
  notifications: (unread?: boolean) => ['notifications', unread ?? false] as const,
  unread: ['notifications-unread'] as const,
  views: ['views'] as const,
};

async function list<T>(path: string): Promise<ApiList<T>> {
  // api.get desembrulha `data`; para listas precisamos de `meta` também.
  const res = await fetch(`/api/v1${path}`, { headers: authHeaders(), credentials: 'same-origin' });
  if (res.status === 401) {
    const session = await api.refresh();
    if (!session) throw new Error('UNAUTHORIZED');
    return list<T>(path);
  }
  const json = (await res.json()) as ApiList<T> & { error?: { message: string } };
  if (!res.ok) throw new Error(json.error?.message ?? 'Erro');
  return json;
}

function authHeaders(): Record<string, string> {
  const token = (api as unknown as { accessToken: string | null }).accessToken;
  return token
    ? { Authorization: `Bearer ${token}`, Accept: 'application/json' }
    : { Accept: 'application/json' };
}

// ---------- Tasks ----------
export function useTasks(q: Partial<TaskListQuery>, enabled = true) {
  return useQuery({
    queryKey: keys.tasks(q),
    queryFn: () => list<TaskSummary>(`/tasks${qs(q)}`),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useTask(id: string | undefined) {
  return useQuery({
    queryKey: keys.task(id ?? ''),
    queryFn: () => api.get<TaskDetail>(`/tasks/${id}`),
    enabled: Boolean(id),
  });
}

export function useFeed(id: string | undefined) {
  return useQuery({
    queryKey: keys.feed(id ?? ''),
    queryFn: () => api.get<FeedItem[]>(`/tasks/${id}/feed`),
    enabled: Boolean(id),
  });
}

export function useMyWork() {
  return useQuery({
    queryKey: keys.myWork,
    queryFn: () => api.get<MyWork>(`/me/work${qs(myWorkRange())}`),
    refetchInterval: 60_000,
  });
}

/** Invalida tudo que depende de tarefas após uma mutação. */
export function useInvalidateTasks() {
  const qc = useQueryClient();
  return (id?: string) => {
    void qc.invalidateQueries({ queryKey: ['tasks'] });
    void qc.invalidateQueries({ queryKey: keys.myWork });
    void qc.invalidateQueries({ queryKey: ['task-feed'] });
    if (id) void qc.invalidateQueries({ queryKey: keys.task(id) });
    else void qc.invalidateQueries({ queryKey: ['task'] });
  };
}

export function useTaskMutation<TVars>(
  fn: (vars: TVars) => Promise<unknown>,
  opts?: { onSuccess?: () => void },
) {
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: fn,
    onSuccess: (data) => {
      const id = (data as { id?: string } | undefined)?.id;
      invalidate(id);
      opts?.onSuccess?.();
    },
  });
}

// ---------- Cadastros ----------
export function useUsers(q?: { q?: string; status?: string; per_page?: number }) {
  return useQuery({
    queryKey: keys.users(q),
    queryFn: () => list<OrgUser>(`/users${qs({ per_page: 200, ...q })}`),
    staleTime: 60_000,
  });
}
export function useRoles() {
  return useQuery({
    queryKey: keys.roles,
    queryFn: () => api.get<Role[]>('/roles'),
    staleTime: 300_000,
  });
}
export function useTeams() {
  return useQuery({
    queryKey: keys.teams,
    queryFn: () => api.get<Team[]>('/teams'),
    staleTime: 60_000,
  });
}
export function useFunctions() {
  return useQuery({
    queryKey: keys.functions,
    queryFn: () => api.get<FunctionRow[]>('/functions'),
    staleTime: 60_000,
  });
}
export function useClients(q?: Record<string, unknown>) {
  return useQuery({
    queryKey: keys.clients(q),
    queryFn: () => list<Client>(`/clients${qs({ per_page: 100, ...q })}`),
    placeholderData: keepPreviousData,
  });
}
export function useClient(id: string | undefined) {
  return useQuery({
    queryKey: keys.client(id ?? ''),
    queryFn: () => api.get<Client>(`/clients/${id}`),
    enabled: Boolean(id),
  });
}
export function useContacts(clientId: string | undefined) {
  return useQuery({
    queryKey: keys.contacts(clientId ?? ''),
    queryFn: () => api.get<ClientContact[]>(`/clients/${clientId}/contacts`),
    enabled: Boolean(clientId),
  });
}
export function useContexts(q?: { type?: string; status?: string; q?: string }) {
  return useQuery({
    queryKey: keys.contexts(q),
    queryFn: () => api.get<Context[]>(`/contexts${qs(q ?? {})}`),
    staleTime: 60_000,
  });
}
export function useTags() {
  return useQuery({
    queryKey: keys.tags,
    queryFn: () => api.get<Tag[]>('/tags'),
    staleTime: 60_000,
  });
}
export function useViews() {
  return useQuery({
    queryKey: keys.views,
    queryFn: () => api.get<SavedView[]>('/views'),
    staleTime: 60_000,
  });
}
export function useNotifications(unread = false) {
  return useQuery({
    queryKey: keys.notifications(unread),
    queryFn: () =>
      list<Notification>(`/notifications${qs({ unread: unread || undefined, per_page: 30 })}`),
  });
}
export function useUnreadCount() {
  return useQuery({
    queryKey: keys.unread,
    queryFn: () => api.get<{ count: number }>('/notifications/unread'),
    refetchInterval: 30_000,
  });
}

/** Mutação genérica que invalida chaves informadas. */
export function useInvalidating<TVars, TData = unknown>(
  fn: (vars: TVars) => Promise<TData>,
  keysToInvalidate: readonly (readonly unknown[])[],
  opts?: { onSuccess?: () => void },
) {
  const qc = useQueryClient();
  return useMutation<TData, Error, TVars>({
    mutationFn: fn,
    onSuccess: () => {
      for (const k of keysToInvalidate) void qc.invalidateQueries({ queryKey: k as unknown[] });
      opts?.onSuccess?.();
    },
  });
}

// ---------- Fase 2: workflows, recorrência, automações, feriados ----------
import type {
  AutomationRule,
  Holiday,
  RecurrenceRule,
  TaskBlock,
  WorkflowInstance,
  WorkflowTemplate,
  WorkflowVersion,
} from '@pm/schemas';

export const wfKeys = {
  templates: ['workflow-templates'] as const,
  template: (id: string) => ['workflow-template', id] as const,
  version: (id: string) => ['workflow-version', id] as const,
  blocks: ['task-blocks'] as const,
  taskWorkflow: (taskId: string) => ['task-workflow', taskId] as const,
  recurrence: (taskId: string) => ['task-recurrence', taskId] as const,
  automations: ['automations'] as const,
  holidays: ['holidays'] as const,
  contextFunctions: (contextId: string) => ['context-functions', contextId] as const,
};

export function useWorkflowTemplates() {
  return useQuery({
    queryKey: wfKeys.templates,
    queryFn: () => api.get<WorkflowTemplate[]>('/workflow-templates'),
    staleTime: 30_000,
  });
}
export function useWorkflowTemplate(id: string | undefined) {
  return useQuery({
    queryKey: wfKeys.template(id ?? ''),
    queryFn: () => api.get<WorkflowTemplate>(`/workflow-templates/${id}`),
    enabled: Boolean(id),
  });
}
export function useWorkflowVersion(id: string | undefined) {
  return useQuery({
    queryKey: wfKeys.version(id ?? ''),
    queryFn: () => api.get<WorkflowVersion>(`/workflow-versions/${id}`),
    enabled: Boolean(id),
  });
}
export function useTaskBlocks() {
  return useQuery({
    queryKey: wfKeys.blocks,
    queryFn: () => api.get<TaskBlock[]>('/task-blocks'),
    staleTime: 30_000,
  });
}
export function useTaskWorkflow(taskId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: wfKeys.taskWorkflow(taskId ?? ''),
    queryFn: () => api.get<WorkflowInstance | null>(`/tasks/${taskId}/workflow`),
    enabled: Boolean(taskId) && enabled,
  });
}
export function useRecurrence(taskId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: wfKeys.recurrence(taskId ?? ''),
    queryFn: () => api.get<RecurrenceRule | null>(`/tasks/${taskId}/recurrence`),
    enabled: Boolean(taskId) && enabled,
  });
}
export function useAutomations() {
  return useQuery({
    queryKey: wfKeys.automations,
    queryFn: () => api.get<AutomationRule[]>('/automations'),
    staleTime: 30_000,
  });
}
export function useHolidays() {
  return useQuery({
    queryKey: wfKeys.holidays,
    queryFn: () => api.get<Holiday[]>('/holidays'),
    staleTime: 60_000,
  });
}
export function useContextFunctions(contextId: string | undefined) {
  return useQuery({
    queryKey: wfKeys.contextFunctions(contextId ?? ''),
    queryFn: () =>
      api.get<
        {
          function: { id: string; name: string };
          user: { id: string; name: string; email: string };
        }[]
      >(`/contexts/${contextId}/functions`),
    enabled: Boolean(contextId),
  });
}

// ---------- Fase 3: formulários e solicitações externas ----------
import type {
  ExternalRequest,
  ExternalRequestCounts,
  ExternalRequestListQuery,
  Form,
} from '@pm/schemas';

export const formKeys = {
  forms: ['forms'] as const,
  form: (id: string) => ['form', id] as const,
  requests: (q: Partial<ExternalRequestListQuery>) => ['external-requests', q] as const,
  request: (id: string) => ['external-request', id] as const,
  counts: ['external-requests-counts'] as const,
};

export function useForms() {
  return useQuery({ queryKey: formKeys.forms, queryFn: () => api.get<Form[]>('/forms') });
}
export function useForm(id: string | undefined) {
  return useQuery({
    queryKey: formKeys.form(id ?? ''),
    queryFn: () => api.get<Form>(`/forms/${id}`),
    enabled: Boolean(id),
  });
}
export function useExternalRequests(q: Partial<ExternalRequestListQuery>) {
  return useQuery({
    queryKey: formKeys.requests(q),
    queryFn: () => list<ExternalRequest>(`/external-requests${qs({ per_page: 50, ...q })}`),
    placeholderData: keepPreviousData,
  });
}
export function useExternalRequest(id: string | undefined) {
  return useQuery({
    queryKey: formKeys.request(id ?? ''),
    queryFn: () => api.get<ExternalRequest>(`/external-requests/${id}`),
    enabled: Boolean(id),
  });
}
export function useExternalRequestCounts(enabled = true) {
  return useQuery({
    queryKey: formKeys.counts,
    queryFn: () => api.get<ExternalRequestCounts>('/external-requests/counts'),
    enabled,
    refetchInterval: 60_000,
  });
}

// ---------- Fase 4: tempo, campos customizados, calendário, dashboard e relatórios ----------
import type {
  CalendarRange,
  CustomFieldDefinition,
  CustomFieldEntity,
  CustomFieldValue,
  Dashboard,
  Report,
  TaskTime,
  TimeEntry,
  Weekly,
} from '@pm/schemas';

export const mgKeys = {
  taskTime: (taskId: string) => ['task-time', taskId] as const,
  timer: ['me-timer'] as const,
  fields: (entity?: CustomFieldEntity) => ['custom-fields', entity ?? 'ALL'] as const,
  values: (entity: CustomFieldEntity, id: string) => ['custom-field-values', entity, id] as const,
  calendar: (q: Record<string, unknown>) => ['calendar', q] as const,
  dashboard: (q: Record<string, unknown>) => ['dashboard', q] as const,
  weekly: (q: Record<string, unknown>) => ['weekly', q] as const,
  report: (kind: string, q: Record<string, unknown>) => ['report', kind, q] as const,
};

export function useTaskTime(taskId: string | undefined) {
  return useQuery({
    queryKey: mgKeys.taskTime(taskId ?? ''),
    queryFn: () => api.get<TaskTime>(`/tasks/${taskId}/time`),
    enabled: Boolean(taskId),
  });
}
export function useRunningTimer() {
  return useQuery({
    queryKey: mgKeys.timer,
    queryFn: () => api.get<TimeEntry | null>('/me/timer'),
    refetchInterval: 60_000,
  });
}
export function useCustomFields(entity?: CustomFieldEntity) {
  return useQuery({
    queryKey: mgKeys.fields(entity),
    queryFn: () => api.get<CustomFieldDefinition[]>(`/custom-fields${qs({ entity_type: entity })}`),
    staleTime: 60_000,
  });
}
export function useCustomFieldValues(entity: CustomFieldEntity, id: string | undefined) {
  const path = entity === 'TASK' ? 'tasks' : 'clients';
  return useQuery({
    queryKey: mgKeys.values(entity, id ?? ''),
    queryFn: () => api.get<CustomFieldValue[]>(`/${path}/${id}/custom-fields`),
    enabled: Boolean(id),
  });
}
export function useCalendar(q: {
  from: string;
  to: string;
  context_id?: string;
  mine?: boolean;
  include_tasks?: boolean;
}) {
  return useQuery({
    queryKey: mgKeys.calendar(q),
    queryFn: () => api.get<CalendarRange>(`/calendar/events${qs(q)}`),
    placeholderData: keepPreviousData,
  });
}
export function useDashboard(q: {
  from?: string;
  to?: string;
  context_id?: string;
  team_id?: string;
}) {
  return useQuery({
    queryKey: mgKeys.dashboard(q),
    queryFn: () => api.get<Dashboard>(`/reports/dashboard${qs(q)}`),
    placeholderData: keepPreviousData,
  });
}
export function useWeekly(q: { date?: string; context_id?: string; responsible_user_id?: string }) {
  return useQuery({
    queryKey: mgKeys.weekly(q),
    queryFn: () => api.get<Weekly>(`/reports/weekly${qs(q)}`),
    placeholderData: keepPreviousData,
  });
}
export function useReport(kind: string, q: Record<string, unknown>) {
  return useQuery({
    queryKey: mgKeys.report(kind, q),
    queryFn: () => api.get<Report>(`/reports/${kind}${qs(q)}`),
    placeholderData: keepPreviousData,
  });
}

/** Download autenticado (CSV) sem passar pelo envelope JSON. */
export async function downloadFile(path: string, filename: string): Promise<void> {
  const res = await fetch(`/api/v1${path}`, { headers: authHeaders(), credentials: 'same-origin' });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: { message: string } } | null;
    throw new Error(body?.error?.message ?? `Falha ao exportar (${res.status}).`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ---------- Fase 5: integrações ----------
import type {
  CalendarIntegrationsState,
  ChannelStatus,
  IntegrationDelivery,
  IntegrationsHealth,
  NotificationPreference,
} from '@pm/schemas';

export const intKeys = {
  prefs: ['notification-preferences'] as const,
  channels: ['channels'] as const,
  calendars: ['calendar-integrations'] as const,
  health: ['integrations-health'] as const,
  deliveries: (q: Record<string, unknown>) => ['integration-deliveries', q] as const,
};

export function useNotificationPreferences() {
  return useQuery({
    queryKey: intKeys.prefs,
    queryFn: () => api.get<NotificationPreference[]>('/me/notification-preferences'),
  });
}
export function useChannels() {
  return useQuery({
    queryKey: intKeys.channels,
    queryFn: () => api.get<ChannelStatus>('/me/channels'),
  });
}
export function useCalendarIntegrations() {
  return useQuery({
    queryKey: intKeys.calendars,
    queryFn: () => api.get<CalendarIntegrationsState>('/me/calendar-integrations'),
  });
}
export function useIntegrationsHealth() {
  return useQuery({
    queryKey: intKeys.health,
    queryFn: () => api.get<IntegrationsHealth>('/integrations/health'),
    staleTime: 60_000,
  });
}
export function useDeliveries(q: Record<string, unknown>) {
  return useQuery({
    queryKey: intKeys.deliveries(q),
    queryFn: () =>
      list<IntegrationDelivery>(`/integrations/deliveries${qs({ per_page: 50, ...q })}`),
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
  });
}

// ---------- Fase 6: comercial, notas e cofre ----------
import type {
  Contract,
  ContractListQuery,
  ContextNote,
  Credential,
  Proposal,
  ProposalListQuery,
  Service,
} from '@pm/schemas';

export const comKeys = {
  services: (all?: boolean) => ['services', all ?? false] as const,
  proposals: (q: Record<string, unknown>) => ['proposals', q] as const,
  proposal: (id: string) => ['proposal', id] as const,
  contracts: (q: Record<string, unknown>) => ['contracts', q] as const,
  contract: (id: string) => ['contract', id] as const,
  note: (contextId: string) => ['context-note', contextId] as const,
  credentials: (contextId: string) => ['credentials', contextId] as const,
};

export function useServices(includeInactive = false) {
  return useQuery({
    queryKey: comKeys.services(includeInactive),
    queryFn: () =>
      api.get<Service[]>(`/services${qs({ include_inactive: includeInactive || undefined })}`),
    staleTime: 30_000,
  });
}
export function useProposals(q: Partial<ProposalListQuery>) {
  return useQuery({
    queryKey: comKeys.proposals(q),
    queryFn: () => list<Proposal>(`/proposals${qs({ per_page: 50, ...q })}`),
    placeholderData: keepPreviousData,
  });
}
export function useProposal(id: string | undefined) {
  return useQuery({
    queryKey: comKeys.proposal(id ?? ''),
    queryFn: () => api.get<Proposal>(`/proposals/${id}`),
    enabled: Boolean(id),
  });
}
export function useContracts(q: Partial<ContractListQuery>) {
  return useQuery({
    queryKey: comKeys.contracts(q),
    queryFn: () => list<Contract>(`/contracts${qs({ per_page: 50, ...q })}`),
    placeholderData: keepPreviousData,
  });
}
export function useContract(id: string | undefined) {
  return useQuery({
    queryKey: comKeys.contract(id ?? ''),
    queryFn: () => api.get<Contract>(`/contracts/${id}`),
    enabled: Boolean(id),
  });
}
export function useContextNote(contextId: string | undefined) {
  return useQuery({
    queryKey: comKeys.note(contextId ?? ''),
    queryFn: () => api.get<ContextNote>(`/contexts/${contextId}/notes`),
    enabled: Boolean(contextId),
  });
}
export function useCredentials(contextId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: comKeys.credentials(contextId ?? ''),
    queryFn: () => api.get<Credential[]>(`/contexts/${contextId}/credentials`),
    enabled: Boolean(contextId) && enabled,
  });
}
